/**
 * Sim state → mechanic marks for the overlay renderer (pure, no three.js).
 *
 * `MechanicsCollector.collect()` walks the live telegraphs, zones, party debuffs, fixates,
 * tethers and enemy shields once per render frame and fills a reused `MechanicsFrame`
 * (record pools + counts, no allocation in steady state). The renderer only reads it.
 *
 * Positions of units are not copied: marks carry unit ids and the renderer uses the
 * interpolated `RenderUnit` positions, so overhead markers and tethers move smoothly.
 */
import type { AuraDef, AuraId, ContentRegistry, DeepReadonly, Telegraph, UnitId, World, WorldShape, Zone } from '@keystone/sim';

/** 0 = disc (radius `a`, inner radius `b`), 1 = box (half-length `a` along facing, half-width `b`). */
export type MarkShape = 0 | 1;

export interface AreaMark {
  /** Telegraph / zone id (stable across frames). */
  id: string;
  shape: MarkShape;
  /** Centre of the drawn quad (box: the box centre, not the line origin). */
  x: number;
  y: number;
  facing: number;
  a: number;
  b: number;
}

export interface SoakMark extends AreaMark {
  need: number;
  inside: number;
  /** 0..1 over the soak's timer. */
  progress: number;
  resolvesAt: number;
}

export interface BeamMark extends AreaMark {
  /** Unit the beam is anchored on (caster) and tracking. */
  anchorId: UnitId | null;
  targetId: UnitId | null;
  /** Full length (yd) and width (yd) of the beam. */
  length: number;
  width: number;
}

export interface PoolMark extends AreaMark {
  /** Grown radius limit (the quad covers it; a faint dashed ring shows it). */
  max: number;
  /** Growth this frame-ish in yd/s (perSec, plus per-occupant growth). */
  growth: number;
  /** Lifetime left 0..1. */
  life: number;
}

export interface WaveMark extends AreaMark {
  resolvesInMs: number;
  /** Next band of its wave to go off. */
  next: boolean;
  /** Wave identity: caster, ability and cast time shared by its bands. */
  sourceId: UnitId;
  abilityId: string;
  createdAt: number;
}

/** Telegraphed charge line (Hunting Pounce, Burrowing Charge): box from the caster along its facing. */
export interface LaneMark extends AreaMark {
  /** 0..1 over the wind-up. */
  progress: number;
  deadly: boolean;
}

/** Moving hazard zone (bouncing / spiralling axes, rolling waves, chasers). */
export interface MoverMark {
  id: string;
  x: number;
  y: number;
  radius: number;
  /** Velocity (yd/s). */
  vx: number;
  vy: number;
  /** 0 bounce/linear/chase, 1 spiral (spins). */
  spin: number;
  deadly: boolean;
}

/**
 * Untelegraphed frontal cast (Bloodroar, Tail Scythe): the sim only has `EnemyAbility.shape`,
 * so the cone is drawn from the caster while the cast runs. Wide cones (> 180°) show the safe
 * wedge behind the caster.
 */
export interface CastConeMark {
  unitId: UnitId;
  range: number;
  /** Half angle (rad). */
  half: number;
  /** 0..1 cast progress. */
  progress: number;
  deadly: boolean;
}

export type UnitMarkKind = 'spread' | 'isolate' | 'explode';

export interface UnitMark {
  unitId: UnitId;
  auraId: AuraId;
  kind: UnitMarkKind;
  radius: number;
  grows: boolean;
  remainingMs: number;
  durationMs: number;
  stacks: number;
}

export interface LinkMark {
  /** Fixate: the fixating unit id. Tether: the tether id. */
  id: string;
  kind: 'fixate' | 'tether';
  fromId: UnitId;
  toId: UnitId;
  /** Tether: distance / breakAt (≥ 1 = breaking). Fixate: 0. */
  tension: number;
}

export interface ShieldMark {
  unitId: UnitId;
  absorb: number;
  /** Casting the ability this shield makes uninterruptible. */
  gatedCast: boolean;
}

export interface MechanicsFrame {
  soaks: SoakMark[];
  nSoaks: number;
  beams: BeamMark[];
  nBeams: number;
  pools: PoolMark[];
  nPools: number;
  waves: WaveMark[];
  nWaves: number;
  units: UnitMark[];
  nUnits: number;
  links: LinkMark[];
  nLinks: number;
  shields: ShieldMark[];
  nShields: number;
  lanes: LaneMark[];
  nLanes: number;
  movers: MoverMark[];
  nMovers: number;
  cones: CastConeMark[];
  nCones: number;
}

export function emptyMechanicsFrame(): MechanicsFrame {
  return { soaks: [], nSoaks: 0, beams: [], nBeams: 0, pools: [], nPools: 0, waves: [], nWaves: 0, units: [], nUnits: 0, links: [], nLinks: 0, shields: [], nShields: 0, lanes: [], nLanes: 0, movers: [], nMovers: 0, cones: [], nCones: 0 };
}

const area = (): AreaMark => ({ id: '', shape: 0, x: 0, y: 0, facing: 0, a: 0, b: 0 });

/** Place a world shape as a disc or a centred box. Cones are not mechanic overlays (null). */
export function placeShape(ws: DeepReadonly<WorldShape>, out: AreaMark): AreaMark | null {
  const s = ws.shape;
  out.facing = ws.facing;
  switch (s.type) {
    case 'circle':
      out.shape = 0;
      out.x = ws.origin.x;
      out.y = ws.origin.y;
      out.a = s.radius;
      out.b = 0;
      return out;
    case 'ring':
      out.shape = 0;
      out.x = ws.origin.x;
      out.y = ws.origin.y;
      out.a = s.outer;
      out.b = s.inner;
      return out;
    case 'line':
      out.shape = 1;
      out.x = ws.origin.x + Math.cos(ws.facing) * s.length * 0.5;
      out.y = ws.origin.y + Math.sin(ws.facing) * s.length * 0.5;
      out.a = s.length * 0.5;
      out.b = s.width * 0.5;
      return out;
    case 'rect':
      out.shape = 1;
      out.x = ws.origin.x;
      out.y = ws.origin.y;
      out.a = s.h * 0.5;
      out.b = s.w * 0.5;
      return out;
    default:
      return null;
  }
}

/** Cached per-aura classification (content is static). */
interface AuraClass {
  kind: UnitMarkKind | null;
  radius: number;
  grows: boolean;
}

/** Cast-time cone of an enemy ability with no telegraph of its own (null = none). */
interface ConeClass {
  range: number;
  half: number;
  deadly: boolean;
}

function coneOf(content: ContentRegistry, abilityId: string): ConeClass | null {
  const e = content.enemyAbility(abilityId);
  const s = e?.shape;
  if (!e || !s || s.type !== 'cone' || e.kind !== 'frontal') return null;
  // Abilities that place a real telegraph already draw on the floor.
  const telegraphed = (e.effects ?? []).some((x) => x.type === 'telegraph');
  if (telegraphed) return null;
  return { range: s.range, half: Math.min(Math.PI, s.angle / 2), deadly: e.warning?.severity === 'deadly' || !!e.counter?.includes('behindCaster') };
}

const NO_CLASS: AuraClass = { kind: null, radius: 0, grows: false };

function classify(def: AuraDef | undefined): AuraClass {
  if (!def) return NO_CLASS;
  if (def.explodeOnExpire) return { kind: 'explode', radius: def.hazard?.radius ?? def.explodeOnExpire.radius, grows: !!def.hazard?.growsToExpiry };
  if (def.hazard) return { kind: def.hazard.intent, radius: def.hazard.radius, grows: !!def.hazard.growsToExpiry };
  return NO_CLASS;
}

const isHostile = (world: DeepReadonly<World>, id: UnitId): boolean => !world.partyIds.includes(id) && world.units[id]?.faction !== 'party';

const KIND_RANK: Record<UnitMarkKind, number> = { explode: 3, isolate: 2, spread: 1 };

export class MechanicsCollector {
  readonly frame = emptyMechanicsFrame();
  private readonly auraClass = new Map<AuraId, AuraClass>();
  private gateAuras: Set<AuraId> | null = null;
  private readonly coneClass = new Map<string, ConeClass | null>();

  constructor(private readonly content: ContentRegistry) {}

  private classOf(id: AuraId): AuraClass {
    let c = this.auraClass.get(id);
    if (!c) {
      c = classify(this.content.aura(id));
      this.auraClass.set(id, c);
    }
    return c;
  }

  /** Auras that make a cast uninterruptible (EnemyAbility.interruptibleWhen.casterLacksAura). */
  private gates(): Set<AuraId> {
    if (!this.gateAuras) {
      this.gateAuras = new Set();
      for (const id of this.content.abilities.keys()) {
        const g = this.content.enemyAbility(id)?.interruptibleWhen?.casterLacksAura;
        if (g) this.gateAuras.add(g);
      }
    }
    return this.gateAuras;
  }

  collect(world: DeepReadonly<World>, timeMs: number): MechanicsFrame {
    const f = this.frame;
    f.nSoaks = f.nBeams = f.nPools = f.nWaves = f.nUnits = f.nLinks = f.nShields = f.nLanes = f.nMovers = f.nCones = 0;
    const party = world.partyIds;

    for (const t of world.telegraphs) {
      if (!isHostile(world, t.sourceId)) continue;
      if (t.mechanic === 'soak' || (t.intent === 'soak' && t.soak)) this.soak(t, timeMs);
      else if (t.mechanic === 'wave') this.wave(t, timeMs);
      else if (t.mechanic === 'charge') this.lane(t, timeMs);
    }
    this.markNextWaves();
    for (const z of world.zones) {
      if (!z.motion || !isHostile(world, z.sourceId)) continue;
      if (z.motion.spec.type === 'aim') this.beam(z);
      else if (z.motion.spec.type === 'grow') this.pool(z, timeMs);
      else this.mover(z);
    }

    const gates = this.gates();
    for (const id of world.unitOrder) {
      const u = world.units[id];
      if (!u || !u.alive) continue;
      const isParty = u.faction === 'party' || party.includes(id);
      if (isParty) {
        // Most urgent hazard debuff per player (explode > isolate > spread; then soonest).
        let best: UnitMark | null = null;
        for (const a of u.auras) {
          const c = this.classOf(a.auraId);
          if (!c.kind) continue;
          const rem = a.expiresAt === null ? Infinity : a.expiresAt - timeMs;
          if (best && (KIND_RANK[best.kind] > KIND_RANK[c.kind] || (KIND_RANK[best.kind] === KIND_RANK[c.kind] && best.remainingMs <= rem))) continue;
          best = this.unitSlot(best);
          best.unitId = id;
          best.auraId = a.auraId;
          best.kind = c.kind;
          best.radius = c.radius;
          best.grows = c.grows;
          best.remainingMs = rem;
          best.durationMs = a.durationMs;
          best.stacks = a.stacks;
        }
        if (best) f.nUnits++;
        continue;
      }
      if (u.cast) {
        let cc = this.coneClass.get(u.cast.abilityId);
        if (cc === undefined) {
          cc = coneOf(this.content, u.cast.abilityId);
          this.coneClass.set(u.cast.abilityId, cc);
        }
        if (cc) {
          const m = (f.cones[f.nCones++] ??= { unitId: '', range: 0, half: 0, progress: 0, deadly: false });
          m.unitId = id;
          m.range = cc.range;
          m.half = cc.half;
          m.deadly = cc.deadly;
          m.progress = Math.min(1, Math.max(0, (timeMs - u.cast.startedAt) / Math.max(1, u.cast.endsAt - u.cast.startedAt)));
        }
      }
      const fx = u.threat?.fixate;
      if (fx && fx.until > timeMs && world.units[fx.on]?.alive) {
        const l = this.linkSlot();
        l.id = id;
        l.kind = 'fixate';
        l.fromId = id;
        l.toId = fx.on;
        l.tension = 0;
      }
      if (gates.size) {
        for (const a of u.auras) {
          if (!gates.has(a.auraId) || !(a.absorbRemaining && a.absorbRemaining > 0)) continue;
          const s = this.shieldSlot();
          s.unitId = id;
          s.absorb = a.absorbRemaining;
          s.gatedCast = !!u.cast && this.content.enemyAbility(u.cast.abilityId)?.interruptibleWhen?.casterLacksAura === a.auraId;
          break;
        }
      }
    }

    const tethers = world.mechanics?.tethers;
    if (tethers) {
      for (const t of tethers) {
        const s = world.units[t.sourceId];
        const u = world.units[t.unitId];
        if (!s || !u || !u.alive) continue;
        const l = this.linkSlot();
        l.id = t.id;
        l.kind = 'tether';
        l.fromId = t.sourceId;
        l.toId = t.unitId;
        l.tension = Math.hypot(u.pos.x - s.pos.x, u.pos.y - s.pos.y) / Math.max(0.1, t.breakAt);
      }
    }
    return f;
  }

  // ---- record pools ----

  private soak(t: DeepReadonly<Telegraph>, timeMs: number): void {
    const f = this.frame;
    const m = (f.soaks[f.nSoaks] ??= { ...area(), need: 1, inside: 0, progress: 0, resolvesAt: 0 });
    if (!placeShape(t.area, m)) return;
    m.id = t.id;
    m.need = t.soak?.min ?? 1;
    m.inside = t.soak?.inside ?? 0;
    m.progress = Math.min(1, Math.max(0, (timeMs - t.createdAt) / Math.max(1, t.resolvesAt - t.createdAt)));
    m.resolvesAt = t.resolvesAt;
    f.nSoaks++;
  }

  private wave(t: DeepReadonly<Telegraph>, timeMs: number): void {
    const f = this.frame;
    const m = (f.waves[f.nWaves] ??= { ...area(), resolvesInMs: 0, next: false, sourceId: '', abilityId: '', createdAt: 0 });
    if (!placeShape(t.area, m)) return;
    m.id = t.id;
    m.resolvesInMs = t.resolvesAt - timeMs;
    m.next = false;
    m.sourceId = t.sourceId;
    m.abilityId = t.abilityId;
    m.createdAt = t.createdAt;
    f.nWaves++;
  }

  /** The soonest band of each wave (same caster, ability and cast) is "next". */
  private markNextWaves(): void {
    const f = this.frame;
    for (let i = 0; i < f.nWaves; i++) {
      const a = f.waves[i]!;
      let next = true;
      for (let j = 0; j < f.nWaves && next; j++) {
        if (i === j) continue;
        const b = f.waves[j]!;
        if (b.sourceId === a.sourceId && b.abilityId === a.abilityId && b.createdAt === a.createdAt && (b.resolvesInMs < a.resolvesInMs || (b.resolvesInMs === a.resolvesInMs && j < i))) next = false;
      }
      a.next = next;
    }
  }

  private lane(t: DeepReadonly<Telegraph>, timeMs: number): void {
    const f = this.frame;
    const m = (f.lanes[f.nLanes] ??= { ...area(), progress: 0, deadly: false });
    if (!placeShape(t.area, m) || m.shape !== 1) return;
    m.id = t.id;
    m.progress = Math.min(1, Math.max(0, (timeMs - t.createdAt) / Math.max(1, t.resolvesAt - t.createdAt)));
    m.deadly = t.intent === 'deadly';
    f.nLanes++;
  }

  private mover(z: DeepReadonly<Zone>): void {
    const mo = z.motion;
    const s = z.area.shape;
    if (!mo || s.type !== 'circle') return;
    const f = this.frame;
    const m = (f.movers[f.nMovers] ??= { id: '', x: 0, y: 0, radius: 1, vx: 0, vy: 0, spin: 0, deadly: false });
    m.id = z.id;
    m.x = z.area.origin.x;
    m.y = z.area.origin.y;
    m.radius = s.radius;
    m.vx = mo.vel.x;
    m.vy = mo.vel.y;
    m.spin = mo.spec.type === 'spiral' || mo.spec.type === 'bounce' ? 1 : 0;
    m.deadly = z.intent === 'deadly';
    f.nMovers++;
  }

  private beam(z: DeepReadonly<Zone>): void {
    const f = this.frame;
    const m = (f.beams[f.nBeams] ??= { ...area(), anchorId: null, targetId: null, length: 0, width: 0 });
    if (!placeShape(z.area, m) || m.shape !== 1) return;
    m.id = z.id;
    m.anchorId = z.motion?.anchorId ?? null;
    m.targetId = z.motion?.targetId ?? null;
    m.length = m.a * 2;
    m.width = m.b * 2;
    f.nBeams++;
  }

  private pool(z: DeepReadonly<Zone>, timeMs: number): void {
    const spec = z.motion?.spec;
    if (!spec || spec.type !== 'grow') return;
    const f = this.frame;
    const m = (f.pools[f.nPools] ??= { ...area(), max: 0, growth: 0, life: 1 });
    if (!placeShape(z.area, m) || m.shape !== 0) return;
    m.id = z.id;
    m.max = Math.max(m.a, spec.max);
    m.growth = m.a >= spec.max - 1e-3 ? 0 : spec.perSec + (spec.perOccupantSec ?? 0) * z.inside.length;
    m.life = Math.min(1, Math.max(0, (z.expiresAt - timeMs) / Math.max(1, z.expiresAt - z.createdAt)));
    f.nPools++;
  }

  private unitSlot(reuse: UnitMark | null): UnitMark {
    if (reuse) return reuse;
    const f = this.frame;
    return (f.units[f.nUnits] ??= { unitId: '', auraId: '', kind: 'spread', radius: 0, grows: false, remainingMs: 0, durationMs: 1, stacks: 1 });
  }

  private linkSlot(): LinkMark {
    const f = this.frame;
    return (f.links[f.nLinks++] ??= { id: '', kind: 'fixate', fromId: '', toId: '', tension: 0 });
  }

  private shieldSlot(): ShieldMark {
    const f = this.frame;
    return (f.shields[f.nShields++] ??= { unitId: '', absorb: 0, gatedCast: false });
  }
}
